import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Heart, Send } from "lucide-react";
import { Separator } from "./ui/separator";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { toast } from "sonner";
import axios from "axios";
import { SOCIAL_ACCOUNTS } from "../lib/social";

const API = process.env.REACT_APP_BACKEND_URL;

// Social Media Links Component - reusable
export const SocialMediaLinks = ({ size = "md", showLabels = false, className = "" }) => {
  const socialLinks = Object.values(SOCIAL_ACCOUNTS);

  const sizeClasses = {
    sm: "w-5 h-5",
    md: "w-6 h-6",
    lg: "w-8 h-8"
  };

  return (
    <div className={`flex items-center gap-4 ${className}`}>
      {socialLinks.map(social => (
        <a
          key={social.name}
          href={social.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`text-green-200 ${social.hoverColor} transition-colors flex items-center gap-2`}
          aria-label={social.name}
          data-testid={`social-link-${social.name.toLowerCase()}`}
        >
          <social.icon className={sizeClasses[size]} />
          {showLabels && <span className="text-sm">{social.name}</span>}
        </a>
      ))}
    </div>
  );
};

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }
    setSubscribing(true);
    try {
      await axios.post(`${API}/api/newsletter/subscribe`, { email });
      toast.success("Successfully subscribed to the newsletter!");
      setEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to subscribe");
    } finally {
      setSubscribing(false);
    }
  };

  return (
  <footer className="bg-green-900 text-white py-16 relative overflow-hidden">
    <div className="max-w-7xl mx-auto px-6">
      {/* Newsletter Section */}
      <div className="bg-green-800/50 rounded-2xl p-6 md:p-8 mb-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-2xl font-bold mb-2">Subscribe to Our Newsletter</h3>
            <p className="text-green-200">Get updates on new cats, events, and tips delivered to your inbox!</p>
          </div>
          <form onSubmit={handleSubscribe} className="flex gap-2 w-full md:w-auto">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="bg-white/10 border-green-700 text-white placeholder:text-green-300 min-w-[250px]"
              data-testid="newsletter-email"
            />
            <Button 
              type="submit" 
              disabled={subscribing}
              className="bg-amber-600 hover:bg-amber-700 shrink-0"
              data-testid="newsletter-submit"
            >
              <Send className="w-4 h-4 mr-2" /> {subscribing ? "..." : "Subscribe"}
            </Button>
          </form>
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-12">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_feline-friends-11/artifacts/06g3ts97_IMG_0597.jpeg" 
              alt="CatPals+ Logo" 
              className="w-[120px] h-[120px] rounded-full object-cover"
            />
            <span className="text-2xl font-bold font-serif">CatPals+</span>
          </div>
          <p className="text-green-100 text-sm leading-relaxed">
            Norwich's trusted cat rescue and sitting service. Giving every feline a chance at a purr-fect life.
          </p>
        </div>
        <div>
          <h4 className="font-semibold mb-4">Quick Links</h4>
          <ul className="space-y-2 text-green-100 text-sm">
            <li><Link to="/cats" className="hover:text-amber-400 transition-colors">Adopt a Cat</Link></li>
            <li><Link to="/book" className="hover:text-amber-400 transition-colors">Cat Sitting</Link></li>
            <li><Link to="/blog" className="hover:text-amber-400 transition-colors">Blog</Link></li>
            <li><Link to="/community-gallery" className="hover:text-amber-400 transition-colors">Community Gallery</Link></li>
            <li><Link to="/in-memoriam" className="hover:text-amber-400 transition-colors">In Memoriam</Link></li>
            <li><Link to="/volunteer" className="hover:text-amber-400 transition-colors">Volunteer</Link></li>
            <li><Link to="/about" className="hover:text-amber-400 transition-colors">About Us</Link></li>
            <li><Link to="/contact" className="hover:text-amber-400 transition-colors">Contact</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold mb-4">Contact</h4>
          <ul className="space-y-2 text-green-100 text-sm">
            <li className="flex items-center gap-2"><MapPin className="w-4 h-4" /> 8 Harvey Lane, Norwich, NR70BQ</li>
            <li className="flex items-center gap-2"><Phone className="w-4 h-4" /> (01603) 555574</li>
            <li className="flex items-center gap-2"><Mail className="w-4 h-4" /> Team@CatPals.co.uk</li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold mb-4">Hours</h4>
          <ul className="space-y-2 text-green-100 text-sm">
            <li className="flex items-center gap-2"><Clock className="w-4 h-4" /> Mon-Fri: 9am - 6pm</li>
            <li className="flex items-center gap-2"><Clock className="w-4 h-4" /> Sat: 10am - 4pm</li>
            <li className="flex items-center gap-2"><Clock className="w-4 h-4" /> Sun: By appointment</li>
          </ul>
          <div className="mt-6">
            <h4 className="font-semibold mb-3">Follow Us</h4>
            <SocialMediaLinks size="md" />
            <Link to="/social" className="text-xs text-green-300 hover:text-amber-400 mt-2 inline-block">
              View our social feeds →
            </Link>
          </div>
        </div>
      </div>
      <Separator className="my-8 bg-green-800" />
      <div className="flex flex-col md:flex-row justify-between items-center text-sm text-green-200">
        <p>&copy; 2026 CatPals+ Norwich. All rights reserved.</p>
        <div className="flex items-center gap-4 mt-2 md:mt-0">
          <Link to="/privacy-policy" className="hover:text-amber-400 transition-colors">Privacy Policy</Link>
          <span>|</span>
          <Link to="/terms" className="hover:text-amber-400 transition-colors">Terms & Conditions</Link>
        </div>
        <div className="flex items-center gap-3 mt-2 md:mt-0">
          <span className="text-green-300">Secure payments via</span>
          <img 
            src="https://www.paypalobjects.com/webstatic/mktg/Logo/pp-logo-200px.png" 
            alt="PayPal" 
            className="h-6"
          />
        </div>
      </div>
      <div className="text-center mt-4 text-sm text-green-300">
        Made with <Heart className="w-4 h-4 inline text-amber-400" /> for cats
      </div>
    </div>
  </footer>
  );
};

export default Footer;
